import { useEffect, useState } from 'react'
import { Button, Card, Group, NumberInput, Stack, Switch, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery } from '@tanstack/react-query'
import { IconCoin, IconReceipt } from '@tabler/icons-react'
import { useOrpc } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

type Credential = 'MD' | 'DO' | 'NP' | 'PA' | 'RN'
const CREDENTIALS: Credential[] = ['MD', 'DO', 'NP', 'PA', 'RN']
type Percent = Record<Credential, number | null>

// Data Migrations card for the PD-1597 fee policy: the percent of the MD/DO base
// each credential bills at (a per-code NP/PA price in Settings › Terminology wins),
// plus the one-click load of the ticket's initial charge schedule onto the caller
// organization's procedure catalog. Super-admin only until PD-1489's Billing
// Manager role lands.
export function FeePolicyCard() {
  const orpc = useOrpc()
  const policy = useQuery(orpc.billingFeePolicyGet.queryOptions({ input: {} }))
  const [percent, setPercent] = useState<Percent | null>(null)
  const [selfPayFeeDollars, setSelfPayFeeDollars] = useState<number | ''>('')
  useEffect(() => {
    if (policy.data && !percent) {
      setPercent(policy.data.policy.credentialPercent)
      setSelfPayFeeDollars(policy.data.policy.selfPayVisitFeeCents / 100)
    }
  }, [policy.data, percent])

  const notify = (color: 'green' | 'red', title: string, message: string) =>
    notifications.show({ color, title, message, autoClose: color === 'green' ? 6000 : false })

  const save = useMutation(
    orpc.superSetFeePolicy.mutationOptions({
      onSuccess: () =>
        notify('green', 'Fee policy saved', 'New quotes use the updated percentages.'),
      onError: (error) => {
        logError('super', 'Failed to save the fee policy', error)
        notify('red', 'Save failed', error instanceof Error ? error.message : 'Unknown error')
      },
    }),
  )
  const seed = useMutation(
    orpc.superSeedFeeSchedule.mutationOptions({
      onSuccess: (r) =>
        notify(
          'green',
          'Charge schedule loaded',
          `${r.added} code(s) added and ${r.updated} updated on the catalog (${r.codes} codes total).`,
        ),
      onError: (error) => {
        logError('super', 'Failed to load the charge schedule', error)
        notify('red', 'Load failed', error instanceof Error ? error.message : 'Unknown error')
      },
    }),
  )

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Stack gap="md">
        <div>
          <Text fw={600}>Billing Fee Policy</Text>
          <Text c="dimmed" size="sm">
            Percent of the MD/DO base charge each credential bills at when a code has no NP/PA price
            of its own. Off means the credential cannot bill physician E/M codes. Only your
            organization is affected. The self-pay visit fee is what an uninsured visit pays by
            card.
          </Text>
        </div>
        {percent && (
          <Group gap="md" align="flex-end" wrap="wrap">
            {CREDENTIALS.map((credential) => (
              <Stack key={credential} gap={4}>
                <NumberInput
                  label={credential}
                  w={110}
                  min={0}
                  max={200}
                  suffix="%"
                  disabled={percent[credential] === null}
                  value={percent[credential] ?? ''}
                  onChange={(v) =>
                    setPercent({ ...percent, [credential]: v === '' ? 0 : Number(v) })
                  }
                />
                <Switch
                  size="xs"
                  label="May bill"
                  checked={percent[credential] !== null}
                  onChange={(e) =>
                    setPercent({ ...percent, [credential]: e.currentTarget.checked ? 100 : null })
                  }
                />
              </Stack>
            ))}
            <NumberInput
              label="Self-pay visit fee"
              description="Flat fee an uninsured visit pays, in dollars (PD-1943)."
              w={160}
              min={0}
              decimalScale={2}
              prefix="$"
              value={selfPayFeeDollars}
              onChange={(v) => setSelfPayFeeDollars(v === '' ? '' : Number(v))}
            />
            <Button
              leftSection={<IconCoin size={18} />}
              loading={save.isPending}
              onClick={() =>
                save.mutate({
                  credentialPercent: percent,
                  selfPayVisitFeeCents: Math.round(Number(selfPayFeeDollars || 0) * 100),
                })
              }
            >
              Save policy
            </Button>
          </Group>
        )}
        <Group justify="space-between" align="center">
          <Text c="dimmed" size="sm">
            Load the PD-1597 initial adult charge schedule (MD/DO and NP/PA amounts for the E/M,
            preventive and Medicare wellness codes) onto this organization&apos;s procedure catalog.
            Existing prices for those codes are replaced; other codes are untouched.
          </Text>
          <Button
            variant="light"
            leftSection={<IconReceipt size={18} />}
            loading={seed.isPending}
            onClick={() => seed.mutate({})}
          >
            Load default schedule
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
